import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Eye, Check, Rocket, Loader2, CheckCircle2, XCircle, TicketPercent, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brl } from "@/lib/format";
import { setTenantSlug } from "@/lib/tenant";
import { Button, Card, Field, Input, Segmented } from "@/components/kit";
import { slugify } from "@/components/master/TenantFields";
import { usePlatform } from "@/pages/Tutorial";

const EMPTY = { name: "", slug: "", admin_name: "", email: "", password: "", phone: "", plan_id: "", mode: "free" };

function PlanCard({ p, active, onPick }) {
  return (
    <button type="button" onClick={onPick} className="card text-left flex flex-col gap-1 transition-transform hover:-translate-y-0.5" style={active ? { outline: "2px solid var(--brand)" } : undefined} data-testid={`signup-plan-${p.id}`}>
      <div className="flex items-center gap-2"><div className="h3 flex-1">{p.name}</div>{active && <Check size={18} style={{ color: "var(--brand)" }} />}</div>
      <div className="h2 tnum">{brl(p.price)}<span className="caption"> /mês</span></div>
      <div className="caption">{p.max_units ? `Até ${p.max_units} unidade(s)` : "Unidades ilimitadas"} · {p.max_users ? `até ${p.max_users} usuários` : "usuários ilimitados"}</div>
    </button>
  );
}

function useSlugCheck(slug) {
  const [ok, setOk] = useState(null);
  useEffect(() => {
    if (slug.length < 3) return setOk(null);
    const t = setTimeout(() => api(`/public/slug/${slug}`).then((r) => setOk(r.available)).catch(() => setOk(null)), 400);
    return () => clearTimeout(t);
  }, [slug]);
  return ok;
}

function CouponBox({ planId, coupon, setCoupon, initial }) {
  const [code, setCode] = useState(initial || "");
  const check = useMutation({
    mutationFn: (c) => api(`/public/coupon/${encodeURIComponent(c)}?plan_id=${planId || ""}`),
    onSuccess: (r) => { setCoupon(r); toast.success("Cupom aplicado"); },
    onError: (e) => { setCoupon(null); toast.error(e.message); },
  });
  const { mutate } = check;
  const used = useRef(false);
  useEffect(() => { if (initial && planId && !used.current) { used.current = true; mutate(initial); } }, [initial, planId, mutate]);
  useEffect(() => { if (coupon && planId) mutate(coupon.code); }, [planId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (coupon) return (
    <div className="sm:col-span-2 flex items-center gap-3 rounded-xl p-3" style={{ background: "var(--surface-alt)" }} data-testid="signup-coupon-applied">
      <TicketPercent size={18} style={{ color: "var(--success)" }} />
      <div className="flex-1"><div className="label">{coupon.code} · {coupon.label}</div>{coupon.first_price !== undefined && <div className="caption" data-testid="signup-coupon-price">1ª mensalidade: {brl(coupon.first_price)}</div>}</div>
      <button type="button" className="icon-btn" onClick={() => { setCoupon(null); setCode(""); }} data-testid="signup-coupon-remove"><X size={15} /></button>
    </div>
  );
  return (
    <div className="sm:col-span-2 flex gap-2 items-end">
      <div className="flex-1"><Field label="Cupom de desconto (opcional)"><Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Digite o código" data-testid="signup-coupon-input" /></Field></div>
      <Button variant="secondary" loading={check.isPending} disabled={!code.trim()} onClick={() => mutate(code.trim())} data-testid="signup-coupon-apply">Aplicar</Button>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen px-4 py-10" style={{ background: "var(--surface-alt)" }}>
      <div className="max-w-3xl mx-auto flex flex-col gap-6">{children}</div>
    </div>
  );
}

export default function Signup() {
  const [f, setF] = useState(EMPTY);
  const [touched, setTouched] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { data } = useQuery({ queryKey: ["public-plans"], queryFn: () => api("/public/plans") });
  const plans = useMemo(() => data?.plans || [], [data]);
  useEffect(() => { if (plans.length && !f.plan_id) setF((x) => ({ ...x, plan_id: plans[0].id })); }, [plans, f.plan_id]);
  const avail = useSlugCheck(f.slug);
  const go = useMutation({
    mutationFn: () => api("/public/signup", { method: "POST", body: { ...f, coupon: coupon?.code || null, origin_url: window.location.origin } }),
    onSuccess: (r) => { toast.dismiss(); if (r.free) nav(`/cadastro/sucesso?session_id=${r.session_id}`); else window.location.href = r.checkout_url; },
    onError: (e) => toast.error(e.message),
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const days = data?.trial_days || 7;
  const modes = [{ value: "free", label: `${days} dias grátis sem cartão` }, { value: "trial", label: `${days} dias grátis com cartão` }, { value: "pay", label: "Assinar agora" }];
  const NOTE = {
    free: `Use tudo por ${days} dias sem informar cartão. Depois, assine para continuar cadastrando · seus dados continuam disponíveis para consulta.`,
    trial: `Nada é cobrado agora · a primeira mensalidade é debitada ao fim dos ${days} dias, se você não cancelar.`,
    pay: "A primeira mensalidade é cobrada agora e renovada todo mês automaticamente.",
  };
  return (
    <Shell>
      <div><div className="eyebrow">Cadastro de restaurante</div><h1 className="h1 mt-1">Comece a controlar seu CMV hoje</h1><p className="caption mt-2">Teste grátis por 7 dias, com ou sem cartão, e receba o acesso na hora. Depois a cobrança é mensal · cancele quando quiser.</p></div>
      <DemoInvite />
      {!plans.length ? <Card className="caption" data-testid="signup-no-plans">Nenhum plano disponível no momento.</Card> : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="signup-plans">{plans.map((p) => <PlanCard key={p.id} p={p} active={f.plan_id === p.id} onPick={() => setF({ ...f, plan_id: p.id })} />)}</div>
          <Card className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome do restaurante"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: touched ? f.slug : slugify(e.target.value) })} data-testid="signup-name-input" /></Field>
            <Field label="Endereço de acesso" hint={avail === false ? "Indisponível · escolha outro" : avail ? "Disponível" : "Letras minúsculas, números e hífen"}>
              <Input value={f.slug} onChange={(e) => { setTouched(true); setF({ ...f, slug: slugify(e.target.value) }); }} data-testid="signup-slug-input" style={avail === false ? { borderColor: "var(--danger)" } : undefined} />
            </Field>
            <Field label="Seu nome"><Input value={f.admin_name} onChange={set("admin_name")} data-testid="signup-admin-name-input" /></Field>
            <Field label="Telefone / WhatsApp"><Input value={f.phone} onChange={set("phone")} data-testid="signup-phone-input" /></Field>
            <Field label="E-mail (seu login)"><Input type="email" value={f.email} onChange={set("email")} data-testid="signup-email-input" /></Field>
            <Field label="Senha" hint="Mínimo 6 caracteres"><Input type="password" value={f.password} onChange={set("password")} data-testid="signup-password-input" /></Field>
            <CouponBox planId={f.plan_id} coupon={coupon} setCoupon={setCoupon} initial={(params.get("cupom") || "").toUpperCase()} />
            <div className="sm:col-span-2"><Segmented options={modes} value={f.mode} onChange={(v) => setF({ ...f, mode: v })} testid="signup-mode" /></div>
            <p className="caption sm:col-span-2" data-testid="signup-mode-note">{NOTE[f.mode]}</p>
            <Button icon={Rocket} className="sm:col-span-2" loading={go.isPending} disabled={avail === false} onClick={() => go.mutate()} data-testid="signup-submit-button">{f.mode === "free" ? "Começar teste grátis" : "Continuar para o pagamento"}</Button>
          </Card>
        </>
      )}
      <p className="caption">Já tem acesso? <Link to="/login" className="underline">Entrar</Link></p>
    </Shell>
  );
}

function DemoInvite() {
  const { data } = usePlatform();
  if (!data?.public_demo) return null;
  return (
    <Card className="flex flex-wrap items-center gap-3" data-testid="signup-demo-card">
      <Eye size={18} style={{ color: "var(--brand)" }} />
      <span className="flex-1 min-w-[200px] text-sm"><b>Quer ver antes?</b> Explore um restaurante de demonstração com fichas, conferências e relatórios · sem cadastro.</span>
      <Link to="/demo" className="btn btn-secondary" data-testid="signup-view-demo-button">Ver demonstração</Link>
    </Card>
  );
}

export function SignupSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [tries, setTries] = useState(0);
  const { data } = useQuery({ queryKey: ["signup-status", sid, tries], queryFn: () => api(`/public/signup/status/${sid}`), enabled: !!sid });
  const st = data?.status;
  useEffect(() => {
    if (!st || ["done", "failed", "expired"].includes(st) || tries >= 20) return;
    const t = setTimeout(() => setTries((n) => n + 1), 2000);
    return () => clearTimeout(t);
  }, [st, tries]);
  const enter = () => { setTenantSlug(data.slug); window.location.href = "/login"; };
  return (
    <Shell>
      <Card className="flex flex-col items-start gap-4" data-testid="signup-success-card">
        {st === "done" ? <CheckCircle2 size={32} style={{ color: "var(--success)" }} /> : ["failed", "expired"].includes(st) ? <XCircle size={32} style={{ color: "var(--danger)" }} /> : <Loader2 size={32} className="animate-spin" style={{ color: "var(--brand)" }} />}
        <div className="h2" data-testid="signup-status-text">{st === "done" ? `${data.name} está pronto!` : st === "failed" ? "Não foi possível criar o acesso" : st === "expired" ? "O pagamento não foi concluído" : "Criando seu ambiente…"}</div>
        {st === "done" && (
          <>
            <p className="caption">{data.mode === "pay" ? "Pagamento confirmado." : "Seu teste grátis começou."} Entre com o e-mail <b>{data.email}</b> e a senha que você criou. Guarde seu endereço de acesso:</p>
            <a href={data.access_url} className="font-bold underline break-all" data-testid="signup-access-url">{data.access_url}</a>
            <Button onClick={enter} data-testid="signup-enter-button">Entrar agora</Button>
          </>
        )}
        {st === "failed" && <p className="caption">{data.error} · fale com o suporte, seu pagamento será verificado.</p>}
        {st === "expired" && <Link to="/cadastro" className="underline">Tentar novamente</Link>}
      </Card>
    </Shell>
  );
}
